import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Loader2, Printer, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const TEST_LABELS = { vacuum: "Vacuum Test", bowie_dick: "Bowie-Dick Test", biological: "Biological Indicator (BI)" };

const monthName = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 15).toLocaleString("en-GB", { month: "long", year: "numeric" });
};

export default function AutoclavePrint() {
  const { token } = useAuth();
  const [params] = useSearchParams();
  const month = params.get("month") || new Date().toISOString().slice(0, 7);
  const [machines, setMachines] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/sterilization/machines`, { headers }),
      axios.get(`${API}/sterilization/cycles?month=${month}`, { headers }),
      axios.get(`${API}/sterilization/tests?month=${month}`, { headers }),
    ]).then(([m, c, t]) => {
      setMachines(m.data);
      setCycles([...c.data].sort((a, b) => a.date.localeCompare(b.date) || a.cycle_number - b.cycle_number));
      setTests([...t.data].sort((a, b) => a.date.localeCompare(b.date)));
    }).catch(console.error).finally(() => setLoading(false));
  }, [month, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" />
      </div>
    );
  }

  const autoclaves = machines.filter(m => m.type === "autoclave");
  const dailyTests = tests.filter(t => t.test_type !== "biological");
  const biTests = tests.filter(t => t.test_type === "biological");
  const failedCycles = cycles.filter(c => c.status === "failed").length;
  const failedTests = tests.filter(t => t.result === "fail").length;

  const Result = ({ t }) => (
    <span style={{ color: t.result === "pass" ? "#059669" : "#DC2626", fontWeight: 700 }}>
      {t.test_type === "biological" ? (t.result === "pass" ? "Negative (−)" : "Positive (+)") : (t.result === "pass" ? "PASS" : "FAIL")}
    </span>
  );

  return (
    <div dir="ltr" className="bg-[#F5F5F7] text-slate-900 print-page screen-min-h">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 9mm; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .print-page, .print-page * { visibility: visible !important; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print-page { background: white !important; padding: 0 !important; margin: 0 !important; position: relative !important; inset: auto !important; }
          .doc { box-shadow: none !important; margin: 0 auto !important; padding: 0 !important; border-radius: 0 !important; max-width: 100% !important; width: 100% !important; background: white !important; }
          .rep-table { font-size: 8pt !important; }
          .rep-table th { font-size: 7.5pt !important; padding: 3px 5px !important; }
          .rep-table td { padding: 2.5px 5px !important; }
          .section-block { page-break-inside: auto !important; }
          .rep-table tr { page-break-inside: avoid !important; }
          .signatures { page-break-inside: avoid !important; margin-top: 20px !important; }
          h2 { font-size: 15px !important; }
          .sec-title { font-size: 9px !important; margin: 10px 0 4px !important; }
          .summary-grid { gap: 5px !important; }
          .summary-grid > div { padding: 5px 8px !important; }
          .summary-grid p.val { font-size: 13px !important; }
          .logo-img { height: 32px !important; }
        }
        .print-page { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; }
        @media screen { .screen-min-h { min-height: 100vh; } }
        .rep-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .rep-table th { background: #655dc6; color: #fff; padding: 6px 8px; text-align: left; font-weight: 700; border: 1px solid #5449a8; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .rep-table td { border: 1px solid #D8D4EE; padding: 5px 8px; background: #fff; }
        .rep-table tr:nth-child(even) td { background: #FAF9FD; }
        .sec-title { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: #4a3fa8; font-weight: 700; margin: 18px 0 8px; padding-bottom: 4px; border-bottom: 2px solid #E2E8F0; }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-3 flex justify-between items-center">
          <Button onClick={() => window.close()} variant="ghost" size="sm" className="text-slate-500">
            <ArrowLeft className="w-4 h-4 mr-1" /> Close
          </Button>
          <Button onClick={() => window.print()} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="print-autoclave-report-btn">
            <Printer className="w-4 h-4" /> Print
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 my-4 doc bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="flex justify-between items-start pb-4">
          <div className="flex items-center gap-4">
            <img src="/lucent-logo.png" alt="Lucent" className="h-14 logo-img" />
            <div>
              <h1 className="text-[15px] font-bold text-[#4a3fa8] tracking-wide leading-tight">LUCENT DENTAL CENTER</h1>
              <p className="text-[11px] text-slate-500 uppercase tracking-widest">CSSD / Sterilization Department</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Sterilization Compliance Report</p>
            <p className="text-[13px] font-bold text-slate-800">{monthName(month)}</p>
            <p className="text-[10px] text-slate-500 mt-1">Generated: {new Date().toLocaleDateString("en-GB")}</p>
          </div>
        </div>
        <div className="h-1 rounded-full" style={{ background: "linear-gradient(90deg,#655dc6,#9b8fd4)" }}></div>

        <h2 className="text-xl font-bold text-slate-900 mt-5">Autoclave Monthly Log — Cycles &amp; Sterilization Tests</h2>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-3 mt-4 summary-grid">
          {[
            { label: "Total Cycles", val: cycles.length },
            { label: "Failed Cycles", val: failedCycles, red: failedCycles > 0 },
            { label: "Tests Performed", val: tests.length },
            { label: "Failed Tests", val: failedTests, red: failedTests > 0 },
          ].map((s, i) => (
            <div key={i} className="border border-slate-200 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-widest text-slate-400">{s.label}</p>
              <p className={`val text-xl font-bold ${s.red ? "text-red-600" : "text-[#4a3fa8]"}`}>{s.val}</p>
            </div>
          ))}
        </div>

        {/* Machines */}
        <div className="section-block">
          <p className="sec-title">Autoclave Machines</p>
          <table className="rep-table">
            <thead>
              <tr><th>Code</th><th>Machine</th><th>Serial No.</th><th>Status</th></tr>
            </thead>
            <tbody>
              {autoclaves.map(m => (
                <tr key={m.id}>
                  <td className="font-bold">{m.code}</td>
                  <td>{m.name}</td>
                  <td style={{ fontFamily: "monospace" }}>{m.serial}</td>
                  <td style={{ color: m.status === "active" ? "#059669" : "#DC2626", fontWeight: 700 }}>
                    {m.status === "active" ? "Active" : "OUT OF SERVICE"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Daily tests */}
        <div className="section-block">
          <p className="sec-title">Daily Tests — Vacuum &amp; Bowie-Dick ({dailyTests.length})</p>
          <table className="rep-table">
            <thead>
              <tr><th>Date</th><th>Test</th><th>Machine</th><th>Result</th><th>Attempt</th><th>Action Taken</th><th>Operator</th></tr>
            </thead>
            <tbody>
              {dailyTests.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", color: "#94A3B8" }}>No tests recorded</td></tr>}
              {dailyTests.map(t => (
                <tr key={t.id}>
                  <td>{t.date}</td>
                  <td>{TEST_LABELS[t.test_type]}</td>
                  <td>{t.machine_code}</td>
                  <td><Result t={t} /></td>
                  <td>{t.attempt}</td>
                  <td>{t.machine_shutdown ? <b style={{ color: "#DC2626" }}>MACHINE SHUTDOWN — engineer callout</b> : t.result === "fail" ? "Retest performed" : "—"}</td>
                  <td>{t.operator_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* BI tests */}
        <div className="section-block">
          <p className="sec-title">Weekly Biological Indicator Tests — 3M ATTEST, 24 min incubation ({biTests.length})</p>
          <table className="rep-table">
            <thead>
              <tr><th>Date</th><th>Machine</th><th>Result</th><th>Attempt</th><th>Action Taken</th><th>Notes</th><th>Operator</th></tr>
            </thead>
            <tbody>
              {biTests.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", color: "#94A3B8" }}>No BI tests recorded</td></tr>}
              {biTests.map(t => (
                <tr key={t.id}>
                  <td>{t.date}</td>
                  <td>{t.machine_code}</td>
                  <td><Result t={t} /></td>
                  <td>{t.attempt}</td>
                  <td>{t.recall_required ? <b style={{ color: "#DC2626" }}>SHUTDOWN + INSTRUMENT RECALL</b> : t.machine_shutdown ? <b style={{ color: "#DC2626" }}>MACHINE SHUTDOWN</b> : t.result === "fail" ? "2nd vial retest" : "—"}</td>
                  <td>{t.notes || "—"}</td>
                  <td>{t.operator_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cycles */}
        <div className="section-block">
          <p className="sec-title">Sterilization Cycle Log ({cycles.length})</p>
          <table className="rep-table">
            <thead>
              <tr><th>Date</th><th>#</th><th>Machine</th><th>Program</th><th>Load Contents</th><th>CI</th><th>Status</th><th>Operator</th></tr>
            </thead>
            <tbody>
              {cycles.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "#94A3B8" }}>No cycles recorded</td></tr>}
              {cycles.map(c => (
                <tr key={c.id}>
                  <td>{c.date}</td>
                  <td style={{ fontWeight: 700, color: "#4a3fa8" }}>{c.cycle_number}</td>
                  <td>{c.machine_code}</td>
                  <td>{c.program_label}</td>
                  <td>{c.contents || "—"}</td>
                  <td style={{ color: c.chemical_indicator_pass ? "#059669" : "#DC2626", fontWeight: 700 }}>{c.chemical_indicator_pass ? "Pass" : "Fail"}</td>
                  <td style={{ color: c.status === "completed" ? "#059669" : "#DC2626", fontWeight: 700 }}>{c.status === "completed" ? "Completed" : "Failed"}</td>
                  <td>{c.operator_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Signatures — official sign-off order (7 signatories) */}
        <div className="mt-10 signatures">
          <p className="sec-title">Sign-Off (in order)</p>
          <div className="grid grid-cols-4 gap-x-6 gap-y-8 text-sm">
            {[
              "1. CSSD Technician",
              "2. CSSD Supervisor",
              "3. Medical Director",
              "4. HR Manager",
              "5. Building Engineer Manager",
              "6. Chief Nurse",
              "7. Bio-Med Manager",
            ].map((role) => (
              <div key={role}>
                <div className="h-10 border-b-2 border-slate-300 mb-1"></div>
                <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{role}</p>
                <p className="text-[8px] text-slate-400 mt-0.5">Name / Signature / Date</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-3 border-t border-slate-200 flex items-center justify-between text-[9px] text-slate-400">
          <span>© {new Date().getFullYear()} Lucent Dental Center — Sterilization Compliance Record</span>
          <span className="font-mono">CSSD/{month}</span>
          <span>Generated {new Date().toLocaleString("en-GB")}</span>
        </div>
      </div>
    </div>
  );
}
